"use client";

import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { UserCheck, PiggyBank, Zap, ShieldCheck, ChartBar as BarChart3, ArrowRight, Sparkles, Check, X, MessageSquareQuote } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const keyBenefits = [{
  icon: UserCheck,
  title: "100% Direct Access to Me",
  highlight: "No Account Managers",
  description: "You work directly with me, Yassine Essaadi. Every strategy session, technical audit, and report is handled personally by a senior SEO consultant—never passed to junior assistants."
}, {
  icon: PiggyBank,
  title: "Cost-Effective SEO Investment",
  highlight: "Pure Expertise Value",
  description: "Agencies charge heavy retainers to cover slick sales teams, luxury offices, and middle management. With my independent consultancy, 100% of your budget goes into high-impact organic search growth."
}, {
  icon: Zap,
  title: "Bespoke & Agile Strategy",
  highlight: "No Template Checklists",
  description: "Agencies rely on rigid, standardized monthly packages. I build custom, fluid SEO strategies tailored precisely to your website's codebase, industry landscape, and specific business goals."
}, {
  icon: ShieldCheck,
  title: "Personal Accountability",
  highlight: "My Name is on the Line",
  description: "As an independent consultant, my business success depends entirely on your campaign results. I take complete personal ownership of your search visibility and lead pipeline."
}, {
  icon: BarChart3,
  title: "Transparent, Honest Reporting",
  highlight: "Revenue Over Vanity Metrics",
  description: "No confusing 50-page automated PDF reports designed to hide lack of progress. You get clear, honest updates focusing on keyword rankings, qualified leads, and measurable business growth."
}, {
  icon: Sparkles,
  title: "Rapid Implementation Speed",
  highlight: "Days, Not Weeks",
  description: "Decisions don't get trapped in agency approval bottlenecks or client-manager games. Direct communication allows us to review, iterate, and deploy technical changes swiftly."
}];
const comparisonMatrix = [{
  factor: "Primary Contact",
  consultant: "Yassine Essaadi (Senior Consultant)",
  agency: "Junior Account Manager (Rotates frequently)"
}, {
  factor: "Strategy Execution",
  consultant: "Tailored to your specific revenue goals",
  agency: "Generic monthly checklist for all clients"
}, {
  factor: "Budget Efficiency",
  consultant: "100% allocated to direct SEO execution",
  agency: "High overheads for sales, offices & management"
}, {
  factor: "Turnaround Speed",
  consultant: "Direct communication & swift execution",
  agency: "Slow multi-layer approval processes"
}, {
  factor: "Accountability",
  consultant: "Personal dedication & direct reputation",
  agency: "Spread across dozens of account portfolios"
}];
export default function ConsultantBenefits() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".reveal-item", {
        y: 30
      });
      gsap.to(".reveal-item", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.1,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".reveal-item", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  return <section id="consultantvsagencybenefits" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      {/* Background Glows */}
      <div className="absolute top-1/4 -left-32 w-96 h-96 bg-accent/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 -right-32 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />

      <div ref={containerRef} className="container mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16 lg:mb-20">
          <Badge variant="secondary" className="mb-4 bg-secondary text-secondary-foreground border border-accent/20 px-3.5 py-1 text-xs font-semibold uppercase tracking-wider rounded-full inline-flex items-center gap-1.5">
            <Sparkles className="size-3.5 text-accent" />
            Direct Expertise vs Agency Retainers
          </Badge>
          
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-serif leading-tight">
            Why Work Directly With Me <span className="text-primary">vs. An SEO Agency?</span>
          </h2>
          
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            When you hire an agency, you often pay for overheads, sales teams, and junior account managers. 
            When you hire YE Consultancy, you work directly with me—getting 100% senior-level expertise focused on your sustainable search growth.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-20">
          {keyBenefits.map((item, i) => {
          const IconComponent = item.icon;
          return <Card key={i} data-index={i} className="reveal-item bg-card border border-border shadow-sm hover:shadow-md hover:border-accent/40 transition-all duration-300 rounded-xl overflow-hidden flex flex-col justify-between" style={{
            opacity: 0
          }}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between mb-3">
                    <div className="p-3 rounded-lg bg-accent/10 border border-accent/20 text-accent inline-flex">
                      <IconComponent className="size-6" />
                    </div>
                    <span className="text-xs font-medium text-accent bg-secondary/80 px-2.5 py-1 rounded-full border border-accent/20">
                      {item.highlight}
                    </span>
                  </div>
                  <CardTitle className="text-xl font-semibold font-serif text-foreground">
                    {item.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                    {item.description}
                  </CardDescription>
                </CardContent>
              </Card>;
        })}
        </div>

        {/* Personal Quote Banner */}
        <div className="reveal-item bg-muted/60 border border-border rounded-2xl p-6 md:p-10 mb-20 relative overflow-hidden" style={{
        opacity: 0
      }}>
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8 relative z-10">
            <div className="shrink-0">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_consultant.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_consultant.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_consultant.webp-wvc-sizes" alt="Yassine Essaadi - Freelance SEO Consultant" loading="lazy" decoding="async" className="size-20 md:size-24 rounded-full object-cover border-2 border-primary shadow-md" />
            </div>

            <div className="flex-1 text-center md:text-left">
              <div className="inline-flex items-center gap-2 text-accent font-semibold text-sm mb-2">
                <MessageSquareQuote className="size-4" />
                <span>My Personal Commitment To You</span>
              </div>
              <blockquote className="text-lg md:text-xl font-medium text-foreground italic font-serif leading-relaxed">
                {"\"I don't operate like a high-volume agency. I limit the number of clients I work with so I can personally deliver strategic, technical, and content SEO that actually moves the needle for your business.\""}
              </blockquote>
              <p className="mt-3 text-sm font-semibold text-foreground">
                Yassine Essaadi <span className="text-muted-foreground font-normal">— Independent Freelance SEO Consultant</span>
              </p>
            </div>
          </div>
        </div>

        {/* Matrix Comparison Table */}
        <div className="reveal-item max-w-4xl mx-auto bg-card border border-border rounded-2xl shadow-sm overflow-hidden mb-16" style={{
        opacity: 0
      }}>
          <div className="bg-muted px-6 py-5 border-b border-border text-center md:text-left">
            <h3 className="text-xl font-bold font-serif text-foreground">
              Direct Comparison At A Glance
            </h3>
            <p className="text-xs md:text-sm text-muted-foreground mt-1">
              See how working directly with an independent specialist compares to a standard agency setup.
            </p>
          </div>

          <div className="divide-y divide-border">
            {/* Table Header */}
            <div className="hidden md:grid md:grid-cols-12 px-6 py-3 bg-card font-semibold text-xs text-muted-foreground uppercase tracking-wider">
              <div className="col-span-4">Factor</div>
              <div className="col-span-4 text-primary font-bold">YE Consultancy (Yassine)</div>
              <div className="col-span-4 text-muted-foreground">Traditional SEO Agency</div>
            </div>

            {/* Table Rows */}
            {comparisonMatrix.map((row, i) => <div key={i} data-index={i} className="p-5 md:p-6 grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-0 items-center hover:bg-muted/30 transition-colors">
                <div className="md:col-span-4 font-semibold text-sm text-foreground">
                  {row.factor}
                </div>
                
                <div className="md:col-span-4 flex items-start gap-2.5">
                  <div className="mt-0.5 rounded-full p-0.5 bg-accent/20 text-accent shrink-0">
                    <Check className="size-3.5" />
                  </div>
                  <span className="text-sm font-medium text-foreground">
                    {row.consultant}
                  </span>
                </div>

                <div className="md:col-span-4 flex items-start gap-2.5">
                  <div className="mt-0.5 rounded-full p-0.5 bg-destructive/10 text-destructive shrink-0">
                    <X className="size-3.5" />
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {row.agency}
                  </span>
                </div>
              </div>)}
          </div>
        </div>

        {/* CTA Box */}
        <div className="reveal-item text-center max-w-2xl mx-auto pt-6" style={{
        opacity: 0
      }}>
          <h3 className="text-2xl font-bold font-serif text-foreground mb-3">
            Ready to work directly with a senior SEO consultant?
          </h3>
          <p className="text-muted-foreground text-sm md:text-base mb-8 leading-relaxed">
            Let’s discuss your website's organic visibility, technical health, and growth potential in a complimentary 1-on-1 consultation.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" className="w-full sm:w-auto font-medium shadow-md group" asChild>
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="size-4 ml-1 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>

            <Button size="lg" variant="outline" className="w-full sm:w-auto font-medium" asChild>
              <Link to="/CaseStudiesPage">
                Explore My Case Studies
              </Link>
            </Button>
          </div>
        </div>

      </div>
    </section>;
}